/**
 * Date Helper Utilities
 * Helper functions for date manipulation in calendar
 */

import { monthNames } from '@/constants/calendar';
import { ViewMode } from '@/types/calendar';

export const getDaysInMonth = (currentDate: Date): (number | null)[] => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days: (number | null)[] = [];

  // Add empty cells for previous month
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  // Add days of current month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  return days;
};

export const getWeekDays = (currentDate: Date): Date[] => {
  const startOfWeek = new Date(currentDate);
  const day = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - day;
  startOfWeek.setDate(diff);

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    days.push(date);
  }
  return days;
};

export const getCurrentViewTitle = (viewMode: ViewMode, currentDate: Date): string => {
  if (viewMode === 'year') {
    return `${currentDate.getFullYear()}`;
  } else if (viewMode === 'month') {
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  } else if (viewMode === 'week') {
    const weekDays = getWeekDays(currentDate);
    const startWeek = weekDays[0];
    const endWeek = weekDays[6];

    if (startWeek.getMonth() === endWeek.getMonth()) {
      return `${startWeek.getDate()} - ${endWeek.getDate()} de ${monthNames[startWeek.getMonth()]} ${startWeek.getFullYear()}`;
    } else {
      return `${startWeek.getDate()} ${monthNames[startWeek.getMonth()]} - ${endWeek.getDate()} ${monthNames[endWeek.getMonth()]} ${endWeek.getFullYear()}`;
    }
  } else if (viewMode === 'day') {
    return currentDate.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  return '';
};

/**
 * Check if a date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date | null): boolean => {
  if (!date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

/**
 * Navigate to a specific month (previous or next)
 */
export const navigateMonth = (currentDate: Date, direction: 'prev' | 'next'): Date => {
  const newDate = new Date(currentDate);
  if (direction === 'prev') {
    newDate.setMonth(newDate.getMonth() - 1);
  } else {
    newDate.setMonth(newDate.getMonth() + 1);
  }
  return newDate;
};

/**
 * Format date to YYYY-MM-DD
 */
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get holiday name for a specific date from holiday map
 */
export const getHolidayName = (
  date: Date,
  holidayMap: Map<string, { name: string; localName: string }>
): string | null => {
  const dateKey = formatDateToISO(date);
  const holiday = holidayMap.get(dateKey);
  return holiday ? holiday.localName || holiday.name : null;
};
